import * as array from 'd3-array'
import { scalePoint } from 'd3-scale'
import * as util from 'infographic-util'
import * as d3 from 'd3-selection'
import labels from './labels'
import basicAnimation from './animations/basic-animation'
import * as colors from 'colors'

export const animations = [
  basicAnimation
]

const gradients = (selection, serieColors, transition) => {
  selection.each(function (d) {
    let gradients = d3.select(this).selectAll('linearGradient')
                      .data(() => {
                        return serieColors.map(color => {
                          return [{color, y1: '0%', y2: '100%', direction: 'up'}, {color, y1: '100%', y2: '0%', direction: 'down'}]
                        }).flat()
                      })
      , enter = gradients.enter()
                         .append('linearGradient')

    enter.merge(gradients)
         .attr('x1', '0%') // top to bottom gradient
         .attr('x2', '0%')
         .attr('y1', d => d.y1)
         .attr('y2', d => d.y2)
         .attr('class', d => `timeline-gradient ${d.color}`)
         .attr('id', d => `timeline-gradient-${d.direction}-${d.color}`)

    enter.append('stop')
         .attr('offset', 0)
         .attr('stop-color', d => colors.adjustLightness(colors.lookup(d.color), 25))
    enter.append('stop')
          .attr('offset', 1)
         .attr('stop-color', d => colors.adjustLightness(colors.lookup(d.color), -10))

    transition.end().then(() => {
      gradients.exit().remove()
    }).catch(() => {
      // interrupted transition, ignore
    })
  })
}

const stages = (selection, chart, config) => {
  selection.each(function (d) {
    let step = d.step
      , height = d.height
      , radius = 8
      , stage = d3.select(this)
                  .selectAll('g.stage')
                  .data(d => d.points)

    stage.exit().remove()

    stage = stage.enter()
                 .append('g')
                 .on('click', (e, _d) => {
                    if (_d.clickable) {
                      chart.ee.emit('chart-event', 'select:point', _d, d.block.range)
                    }
                  })
                 .merge(d3.select(this).selectAll('g.stage'))
                 .attr('class', d => `stage is-deemphasizable ${d.color}`)
                 .classed('deemphasize', d => d.deemphasize)
                 .classed('is-clickable', d => d.clickable)
                 .attr('transform', d => {
                   return `translate(${d.offset}, 0)`
                 })

    stage.call(labels, height + radius, step * 2, config)

    // Adds the vertical bar
    stage.selectAll('path')
         .data(d => [d])
         .enter()
         .append('path')
         .merge(stage.selectAll('path'))
         .attr('d', d => {
           let stroke = 5
           return `M -${stroke / 2} ${radius * d.directionMultiplier}
                  v ${height * d.directionMultiplier}
                  a 1,1 0 0 ${d.directionMultiplier > 0 ? 0 : 1} ${stroke}, 0
                  v ${height * d.directionMultiplier * -1}
                  z`
         })
         .attr('fill', d => `url(#timeline-gradient-${d.direction}-${d.color})`)

    let line = stage.selectAll('line.horizontal')
                    .data((d, i, all)=> {
                      return i !== all.length -1 ? [d] : []
                    })
    line.enter()
        .append('line')
        .attr('class', 'horizontal')
        .attr('x1', radius)
        .merge(line)
        .attr('x2', step - radius)

    line.exit().remove()

    stage.selectAll('circle.base')
         .data(d => [d])
         .enter()
         .append('circle')
         .attr('class', 'base')
         .merge(stage.selectAll('circle.base'))
         .attr('r', radius)
         .attr('stroke', d => colors.adjustLightness(colors.lookup(d.color), -10))
  })
}

export const preAnimate = function (chart, selection, series) {
  let seriesData = series.selection().data()
    , dimensions = chart.datum().dimensions
    , grid = chart.layout.getGrid(dimensions, 0, 0, .2, 1) // Force a single column
    , padding = 12
    , config = chart.datum().axes.infographic
    , scale = scalePoint()
                .domain(array.range(seriesData.length + 2)) // Add two extra points to the end for spacing
                .range([0, grid.width - padding * 2])
                .round(false) // TODO ?
                .padding(0)
    , serieColors = new Set

  let timelines = grid.blocks.map(block => {
    let points = seriesData.map((serie, idx) => {
      let point = util.getPoint(block.range.id, serie)
      point.idx = idx // stage
      point.offset = scale(idx)
      point.direction = idx % 2 === 0 ? 'up' : 'down'
      point.directionMultiplier = idx % 2 === 0 ? -1 : 1
      serieColors.add(point.color)
      return point
    })

    return {
      height: grid.height / 2, // height for each stage
      block,
      step: scale.step(),
      points
    }
  })

  d3.select(selection.node().ownerSVGElement).select('defs')
    .call(gradients, Array.from(serieColors), chart.defaultTransition)

  selection.each(function (d) {
    let container = d3.select(this)
                      .selectAll('g.timeline-container')
                      .data(timelines)
      , enter = container.enter()
                         .append('g')
                           .attr('class', 'timeline-container')

    container.exit().remove()

    container = container.merge(enter)
                         .attr('transform', d => {
                           let dim = d.block.dimensions
                           return `translate(${padding + dim.x}, ${dim.height / 2 + dim.y})`
                         })

    container.call(stages, chart, config)
  })
}
